import {_} from "../utils";
import TinySlider from "./tiny-slider";
import LazyLoad from "./lazy-load";
import bloomreachProductCard from "../templates/bloomreach-product-card";
import BrHelpers from "./bloomreach";

interface ExponeaRecommendationItem {
    url?: string
    title?: string
    sku?: string
    brand?: string
    product_category?: string
    product_item_scale?: string
    product_preorder?: string
    stock_level?: string
    stock_label?: string
    promo_label?: string
    product_rating?: number
    product_num_of_reviews_store_front?: number
    thumb_image_markup?: string
    price?: number
    [currencyField: string]: unknown
}

interface ProductCardData {
    url?: string
    title?: string
    pid?: string
    brand?: string
    product_category?: string
    product_item_scale?: string
    product_preorder?: string
    stock_level?: string
    stock_label?: string
    promo_label?: string
    product_rating?: number
    product_num_of_reviews_store_front?: number
    thumb_image_markup?: string
    price?: number
    [currencyField: string]: unknown
}

declare global {
    interface ExponeaSDK {
        getRecommendation(options: {
            recommendationId: string
            size: number
            callback: (items: ExponeaRecommendationItem[]) => void
        }): void
    }

    const exponea: ExponeaSDK
}

const EXPONEA_READY_TIMEOUT_MS = 5000;
const EXPONEA_READY_POLL_INTERVAL_MS = 100;

export default class BloomreachEngagementRecommenders {
    private elements = _('bloomreach-engagement-recommender' , 'element').nodes() as HTMLElement[]

    init() : void {
        this.getBloomreachRecommender();
    }

    private triggerLoading(container: HTMLElement, on: boolean): void {
        const loader = container.querySelector('[data-element="bloomreach-engagement-recommender-loading"]');
        if (on) {
            loader?.classList.remove('u-hidden');
        } else {
            loader?.classList.add('u-hidden');
        }
    }

    private waitForExponea(): Promise<boolean> {
        if (typeof exponea !== 'undefined') {
            return Promise.resolve(true);
        }
        return new Promise(resolve => {
            const start = Date.now();
            const interval = setInterval(() => {
                if (typeof exponea !== 'undefined') {
                    clearInterval(interval);
                    resolve(true);
                } else if (Date.now() - start >= EXPONEA_READY_TIMEOUT_MS) {
                    clearInterval(interval);
                    resolve(false);
                }
            }, EXPONEA_READY_POLL_INTERVAL_MS);
        });
    }

    private getBloomreachRecommender(): void {
        this.elements.forEach(element => this.triggerLoading(element, true));
        this.waitForExponea().then(isExponeaReady => {
            this.elements.forEach(element => {
                if (!isExponeaReady) {
                    element.classList.add("u-hidden");
                    return;
                }
                const recommenderId = element.dataset.brRecommenderId ?? '';
                const size = parseInt(element.dataset.brNumberOfProducts ?? '0', 10);
                exponea.getRecommendation({
                    recommendationId: recommenderId,
                    size: size,
                    callback: (items: ExponeaRecommendationItem[]) => {
                        if (items && items.length > 0) {
                            this.triggerLoading(element, false);
                            this.constructProductCards(items, element);
                        } else {
                            element.classList.add("u-hidden");
                        }
                    },
                });
            })
        })
    }

    private initializeBlockComponents(container: HTMLElement): void {
        const sliders = container.querySelectorAll('[data-behaviour="tiny-slider"]');
        if (sliders.length > 0) {
            sliders.forEach((slider) => {
                new TinySlider(slider as HTMLElement);
            })
        }
        container.querySelectorAll(_('image', 'lazyload').str()).forEach((image) => {
            new LazyLoad(image as HTMLElement);
        })
    }

    private showRecommendations(container: HTMLElement): void {
        const element = container.querySelector('[data-element="bloomreach-engagement-recommender-container"]');
        if (element) {
            element.classList.remove('u-hidden');
        }
    }

    private mapExponeaItem(item: ExponeaRecommendationItem): ProductCardData {
        return {
            url: item.url,
            title: item.title,
            pid: item.sku,
            brand: item.brand,
            product_category: item.product_category,
            product_item_scale: item.product_item_scale,
            product_preorder: item.product_preorder,
            stock_level: item.stock_level,
            stock_label: item.stock_label,
            promo_label: item.promo_label,
            product_rating: item.product_rating,
            product_num_of_reviews_store_front: item.product_num_of_reviews_store_front,
            thumb_image_markup: item.thumb_image_markup,
            price: item.price,
            [BrHelpers.getCurrencyFilter()]: item[BrHelpers.getCurrencyFilter()],
            [BrHelpers.getOriginalPriceCurrencyFilter()]: item[BrHelpers.getOriginalPriceCurrencyFilter()],
        }
    }

    private constructProductCards(products: ExponeaRecommendationItem[], container: HTMLElement): void {
        const translation = container.dataset.productCardTranslation ?? ''
        const html = products.map((item, i) => bloomreachProductCard(translation, this.mapExponeaItem(item), i)).join('')
        const sliderContainer = container.querySelector('[data-behaviour="tiny-slider"]')?.querySelector('[data-element="container"]');
        if (sliderContainer) {
            sliderContainer.innerHTML = html;
        }
        this.initializeBlockComponents(container);
        this.showRecommendations(container);
    }

}

const loader = new BloomreachEngagementRecommenders();
loader.init();